<template>
  <div 
    v-if="isVisible" 
    class="message-robot-dropdown"
    :style="dropdownStyle"
    @click.stop>
    <div class="dropdown-header">
      <div class="header-title">
        <i class="mdi mdi-robot-happy header-icon"></i>
        <span class="header-text">ロボ会話一覧</span>
      </div>
      <button class="close-btn" @click="closeDropdown">
        <i class="mdi mdi-close"></i>
      </button>
    </div>
    <div class="dropdown-content">
      <div v-if="isLoading" class="loading-container">
        <div class="loading-content">
          <div class="loading-spinner">
            <div class="spinner"></div>
          </div>
          <p class="loading-text">会話履歴を読み込んでいます...</p>
        </div>
      </div>
      <div v-else-if="robotList.length === 0" class="no-data-container">
        <div class="no-data-content">
          <i class="mdi mdi-robot-off no-data-icon"></i>
          <p class="no-data-text">この応募者はまだロボと会話していません</p>
        </div>
      </div>
      <div v-else class="robot-list">
        <div 
          v-for="(robot, index) in robotList" 
          :key="robot.name"
          class="robot-item"
          :class="{ 'goal-completed': robot.isGoal }"
          @click="selectRobot(robot)">
          <div class="robot-info">
            <div class="robot-header">
              <div class="robot-icon-wrapper">
                <img 
                  v-if="robotIcons[index]" 
                  :src="robotIcons[index]" 
                  class="robot-icon"
                  alt="ロボアイコン">
                <img 
                  v-else
                  src="@/assets/template/robot.png" 
                  class="robot-icon"
                  alt="デフォルトロボアイコン">
              </div>
              <div class="robot-details">
                <div class="robot-name">
                  <span>{{ robot.name }}</span>
                  <span v-if="robot.isGoal" class="goal-badge">
                    <i class="mdi mdi-check-circle"></i>
                    ゴール済
                  </span>
                </div>
                <div class="robot-date">
                  <i class="mdi mdi-clock-outline"></i>
                  {{ formatDateRange(robot.from, robot.to) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    position: {
      type: Object,
      default: () => ({ x: 0, y: 0 })
    },
    messageList: {
      type: Array,
      default: () => []
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    goalRobots: {
      type: Array,
      default: () => []
    }
  },
  mounted() {
    if (this.isVisible) {
      setTimeout(() => {
        document.addEventListener('click', this.handleOutsideClick)
      }, 100)
    }
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleOutsideClick)
  },
  watch: {
    isVisible(newVal) {
      if (newVal) {
        // クリックイベントの伝播が完了するまで十分に待つ
        setTimeout(() => {
          document.addEventListener('click', this.handleOutsideClick)
        }, 100)
      } else {
        document.removeEventListener('click', this.handleOutsideClick)
      }
    }
  },
  computed: {
    dropdownStyle() {
      return {
        position: 'fixed',
        left: this.position.x + 'px',
        top: this.position.y + 'px',
        zIndex: 1000
      }
    },
    robotList() {
      // ローディング中やメッセージリストがない場合は空配列を返す
      if (!this.messageList) {
        return [];
      }
      
      // TODO: [DUPLICATE CODE] このロボットリスト処理ロジックは以下2箇所と重複しています:
      // - ManageView.vue (L3830-3876頃)
      // - MessageListTable.vue (L39-78頃)
      // 将来的に共通ユーティリティ関数(utils/robotListProcessor.js)への統一を推奨
      const roboNameList = [];
      const prevRoboName = [];
      const roboGoalStatus = {}; // ロボごとのゴール状態を保存
      var roboName = "";
      var createdAtFrom = new Date();
      var createdAtTo = new Date();

      // goalRobotsからゴール済みロボのbot_idまたはbot_nameを取得してマップ作成
      const goalRobotMap = {};
      if (this.goalRobots && this.goalRobots.length > 0) {
        this.goalRobots.forEach((robot, index) => {
          if (robot.bot_id) {
            goalRobotMap[robot.bot_id] = true;
          }
          if (robot.bot_name) {
            goalRobotMap[robot.bot_name] = true;
          }
        });
      }

      this.messageList.forEach(function (roboname, messageIndex) {
        // ゴール状態をチェック - goalRobotMapからのみ確認
        // bot_idがある場合はbot_idでチェック
        if (roboname.bot_id && goalRobotMap[roboname.bot_id]) {
          roboGoalStatus[roboname.name] = true;
        }
        // bot_nameでもチェック
        else if (goalRobotMap[roboname.name]) {
          roboGoalStatus[roboname.name] = true;
        }
        
        if (prevRoboName[0] !== roboname.name && roboname.speaker === "my") {
          if (roboName !== "") {
            const index = roboNameList.findIndex((el) => el.name === roboName)
            if (index === -1) {
              roboNameList.push({
                name: roboName, 
                from: createdAtFrom, 
                to: createdAtTo,
                isGoal: roboGoalStatus[roboName] || false
              });
            } else {
              roboNameList.splice(index, 1, {
                name: roboNameList[index].name, 
                from: roboNameList[index].from, 
                to: createdAtTo,
                isGoal: roboGoalStatus[roboName] || roboNameList[index].isGoal
              })
            }
          }

          roboName = roboname.name
          createdAtFrom = roboname.createdAt
          createdAtTo = roboname.createdAt
          prevRoboName[0] = roboname.name
        } else {
          createdAtTo = roboname.createdAt
        }
      }); 

      if (roboName !== "") {
        const index = roboNameList.findIndex((el) => el.name === roboName)
        if (index === -1) {
          roboNameList.push({
            name: roboName, 
            from: createdAtFrom, 
            to: createdAtTo,
            isGoal: roboGoalStatus[roboName] || false
          });
        } else {
          roboNameList.splice(index, 1, {
            name: roboNameList[index].name, 
            from: roboNameList[index].from, 
            to: createdAtTo,
            isGoal: roboGoalStatus[roboName] || roboNameList[index].isGoal
          })
        }
      }

      const roboNameList2 = roboNameList.sort((a,b) => {
        return (a.to < b.to) ? -1 : (a.to > b.to) ? 1 : 0;
      })

      return roboNameList2.slice().reverse();
    },
    robotIcons() {
      return this.robotList.map(robot => this.getRobotIcon(robot));
    }
  },
  methods: {
    handleOutsideClick(e) {
      const dropdown = this.$el
      if (!dropdown.contains(e.target)) {
        this.closeDropdown()
      }
    },
    closeDropdown() {
      this.$emit('close');
    },
    selectRobot(robot) {
      this.$emit('selectRobot', robot);
    },
    formatDateRange(from, to) {
      const fromDate = this.$moment(from).format('MM/DD H:mm');
      const toDate = this.$moment(to).format('MM/DD H:mm');
      return `${fromDate}～${toDate}`;
    },
    getRobotIcon(robot) {
      // goalRobots（robotalk_url）から画像URLを取得
      // ゴール済みロボ（ミニロボ含む）の画像URLは既に含まれている
      if (this.goalRobots && this.goalRobots.length > 0) {
        // まずbot_nameで検索
        let goalRobot = this.goalRobots.find(r => r.bot_name === robot.name);
        
        // bot_nameで見つからなければ、bot_idで検索
        if (!goalRobot && robot.isGoal) {
          // ゴール済みのロボはbot_idがあるはず
          // messageListからbot_idを取得
          const messageWithBotId = this.messageList.find(m => 
            m.name === robot.name && m.bot_id
          );
          
          if (messageWithBotId) {
            goalRobot = this.goalRobots.find(r => 
              r.bot_id === messageWithBotId.bot_id
            );
          }
        }
        
        if (goalRobot && goalRobot.image_data) {
          return goalRobot.image_data;
        }
      }
      
      return null;
    }
  }
}
</script>

<style lang="scss" scoped>
.message-robot-dropdown {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  min-width: 320px;
  max-width: 380px;
  max-height: 480px;
  overflow: hidden;
  font-family: 'Noto Sans JP', "Montserrat","游ゴシック",YuGothic,"ヒラギノ角ゴ ProN W3","Hiragino Kaku Gothic ProN","メイリオ",Meiryo,sans-serif;
  
  .dropdown-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 20px;
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    border-bottom: 1px solid #dee2e6;
    
    .header-title {
      display: flex;
      align-items: center;
      gap: 8px;
      
      .header-icon {
        font-size: 24px;
        color: #D50474;
      }
      
      .header-text {
        font-size: 18px;
        font-weight: 700;
        color: #495057;
        letter-spacing: 0.5px;
      }
    }
    
    .close-btn {
      cursor: pointer;
      background: transparent;
      border: 1px solid #dee2e6;
      border-radius: 8px;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;
      
      i {
        color: #6c757d;
        font-size: 20px;
      }
      
      &:hover {
        background: #f8f9fa;
        border-color: #D50474;
        
        i {
          color: #D50474;
        }
      }
    }
  }
  
  .dropdown-content {
    max-height: 400px;
    overflow-y: auto;
    background: #fafbfc;
    
    .loading-container, .no-data-container {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 200px;
      padding: 40px 20px;
    }
    
    .loading-content, .no-data-content {
      text-align: center;
    }
    
    .loading-spinner {
      display: flex;
      justify-content: center;
      margin-bottom: 16px;
      
      .spinner {
        width: 48px;
        height: 48px;
        border: 4px solid #f3f4f6;
        border-top-color: #D50474;
        border-radius: 50%;
        animation: spin 1s linear infinite;
      }
    }
    
    .loading-text {
      color: #6b7280;
      font-size: 14px;
      font-weight: 500;
    }
    
    .no-data-icon {
      font-size: 64px;
      color: #d1d5db;
      margin-bottom: 16px;
    }
    
    .no-data-text {
      color: #6b7280;
      font-size: 14px;
      font-weight: 500;
    }
    
    .robot-list {
      background: white;
      
      .robot-item {
        padding: 16px 20px;
        cursor: pointer;
        border-bottom: 1px solid #f3f4f6;
        transition: all 0.2s ease;
        background: white;
        
        &:hover {
          background: #f9fafb;
          box-shadow: inset 4px 0 0 0 #D50474;
        }
        
        &:last-child {
          border-bottom: none;
        }
        
        .robot-info {
          width: 100%;
        }
        
        .robot-header {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        
        .robot-icon-wrapper {
          flex-shrink: 0;
        }
        
        .robot-details {
          flex: 1;
          min-width: 0;
        }
        
        .robot-name {
          font-size: 16px;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 4px;
          display: flex;
          align-items: center;
          gap: 8px;
          
          span:first-child {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }
        
        .robot-date {
          font-size: 13px;
          color: #6b7280;
          display: flex;
          align-items: center;
          gap: 4px;
          
          i {
            font-size: 14px;
          }
        }
        
        
        // ゴール済みロボのスタイリング
        &.goal-completed {
          background: linear-gradient(135deg, #FFE5F1 0%, #FFD6E8 100%);
          
          &:hover {
            background: linear-gradient(135deg, #FFD6E8 0%, #FFC2DD 100%);
            box-shadow: inset 4px 0 0 0 #D50474;
          }
          
          .robot-name {
            color: #1f2937;
          }
          
          .robot-date {
            color: #6b7280;
          }
        }
        
        .goal-badge {
          background: rgba(213, 4, 116, 0.1);
          color: #D50474;
          font-size: 11px;
          font-weight: 600;
          padding: 3px 10px;
          border-radius: 12px;
          border: 1px solid rgba(213, 4, 116, 0.2);
          display: inline-flex;
          align-items: center;
          gap: 3px;
          
          i {
            font-size: 12px;
          }
        }
      }
    }
  }
  
  // スクロールバーのスタイリング
  .dropdown-content::-webkit-scrollbar {
    width: 6px;
  }
  
  .dropdown-content::-webkit-scrollbar-track {
    background: #f3f4f6;
  }
  
  .dropdown-content::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;
  }
  
  .dropdown-content::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
  }
  
  .robot-icon {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid #e5e7eb;
    background: white;
  }
  
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>


// WEBPACK FOOTER //
// src/components/organisms/dropdowns/MessageRobotDropdown.vue